import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { staleReasonCode } from '@/features/approvals/types';
import { panel360Href } from '@/features/evaluations/lib/panel-360';
import { relativeTime } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import { bellHref } from '../lib/bell-href';
import type {
  EntryMovedUpdate,
  EntrySubstituteUpdate,
  ExportReadyUpdate,
  JudgeAddedLateUpdate,
  JudgeReplacedUpdate,
  NeedsActionItem,
  PanelsGeneratedUpdate,
  SignoffBellVariant,
  SignoffResolvedUpdate,
  StaffUpdate,
  Urgency,
} from '../types';

/** The row's left rule and urgency word: red asks for a decision, amber for a follow-up. */
type Tone = 'danger' | 'warning' | 'info' | 'read';

const RULE: Record<Tone, string> = {
  danger: 'before:bg-status-red',
  warning: 'before:bg-status-amber',
  info: 'before:bg-primary',
  read: 'before:bg-transparent',
};

const WORD: Record<Exclude<Tone, 'read'>, string> = {
  danger: 'text-status-red',
  warning: 'text-[#9A3412]',
  info: 'text-primary',
};

const TONE_OF: Record<Urgency, Tone> = { DECISION_NEEDED: 'danger', FOLLOW_UP: 'warning' };

/**
 * "Award · Round", and further parts joined the same way. The separator comes
 * from the locale (`notifications.context`), so it is never hard-coded here.
 */
function joinContext(t: TFunction, parts: Array<string | null | undefined>): string {
  return parts
    .filter((p): p is string => !!p)
    .reduce((acc, part) => (acc ? t('notifications.context', { award: acc, round: part }) : part), '');
}

/**
 * The title and detail of a finished background panel generation (suggested
 * panels R20; the round is on the context line). DONE says how many were
 * created and, when any were skipped, the breakdown by cause — a zero cause is
 * not said. FAILED says why it stopped; an unknown code leaves the reason out
 * rather than showing it raw.
 */
function panelsGeneratedCopy(
  t: TFunction,
  language: string | undefined,
  update: PanelsGeneratedUpdate,
): { title: string; detail: string } {
  const p = 'notifications.update.PANELS_GENERATED';
  if (update.outcome === 'FAILED') {
    const reason = update.failureCode
      ? t(`judgingSetup.suggest.bg.failure.${update.failureCode}`, { defaultValue: '' })
      : '';
    return { title: t(`${p}.titleFailed`), detail: t(`${p}.failed`, { reason }).trim() };
  }
  const { exists, nameTaken, changed } = update.skipped;
  const parts = [
    exists > 0 ? t(`${p}.skipExists`, { count: exists }) : null,
    nameTaken > 0 ? t(`${p}.skipNameTaken`, { count: nameTaken }) : null,
    changed > 0 ? t(`${p}.skipChanged`, { count: changed }) : null,
  ].filter((part): part is string => part !== null);
  const skipped = exists + nameTaken + changed;
  const detail = [t(`${p}.detail`, { count: update.created })];
  if (skipped > 0) {
    const breakdown = new Intl.ListFormat(language, { style: 'long', type: 'unit' }).format(parts);
    detail.push(t(`${p}.detailSkipped`, { count: skipped, breakdown }));
  }
  return { title: t(`${p}.title`), detail: detail.join(' ') };
}

/** A name worth showing, or null: an empty or blank one never reaches the copy. */
function named(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * The title and detail of a judge added after judging started (#39 slice 6,
 * D2): "Kicukiro panel: Claudine Mukamana added late", then how many entries
 * they took on and which. A judge or panel that cannot be named reads
 * generically, never as an empty string. The organiser's reason is not on the
 * payload and is never shown here.
 */
function judgeAddedLateCopy(t: TFunction, update: JudgeAddedLateUpdate): { title: string; detail: string } {
  const p = 'notifications.update.JUDGE_ADDED_LATE';
  const judge = named(update.judge?.displayName);
  const panel = named(update.panelName);
  const title =
    judge && panel
      ? t(`${p}.titleNamed`, { judge, panel })
      : judge
        ? t(`${p}.titleJudge`, { judge })
        : panel
          ? t(`${p}.titlePanel`, { panel })
          : t(`${p}.title`);
  const detail = t(update.scope === 'ALL' ? `${p}.detailAll` : `${p}.detailUnscored`, { count: update.entries });
  return { title, detail };
}

/**
 * A judge replaced on a panel already judging (#39 slice 6, D1): "Gasabo
 * panel: Aline Uwase replaced by Jean Bosco Habimana", then what was handed
 * over and what still counts. Both judges must be named to name either;
 * otherwise the row reads generically. The organiser's reason is never here.
 * A substitute's replace says "as substitute", and on one entry names it
 * (fix round 3, R3), so it never reads as a seat changing hands.
 */
function judgeReplacedCopy(t: TFunction, update: JudgeReplacedUpdate): { title: string; detail: string } {
  const p = 'notifications.update.JUDGE_REPLACED';
  const outgoing = named(update.outgoing?.displayName);
  const replacement = named(update.replacement?.displayName);
  const panel = named(update.panelName);
  const title =
    update.substitute === true || update.entry
      ? substituteReplacedTitle(t, update, { outgoing, replacement, panel })
      : outgoing && replacement
        ? panel
          ? t(`${p}.titleNamed`, { panel, outgoing, replacement })
          : t(`${p}.titleJudges`, { outgoing, replacement })
        : panel
          ? t(`${p}.titlePanel`, { panel })
          : t(`${p}.title`);
  const detail = [
    update.handedOver > 0 ? t(`${p}.handedOver`, { count: update.handedOver }) : t(`${p}.handedOverNone`),
    update.kept > 0 ? t(`${p}.kept`, { count: update.kept }) : null,
    update.draftsKept > 0 ? t(`${p}.draftsKept`, { count: update.draftsKept }) : null,
  ].filter((part): part is string => part !== null);
  return { title, detail: detail.join(' ') };
}

/**
 * The title of a substitute's replace: "Gasabo panel: Aline Uwase replaced by
 * Jean Bosco Habimana as substitute on Imena Dance Studio", "… on an entry"
 * when the entry has no name, "… as substitute" across the panel; the same
 * without the panel when only its name is missing; generic substitute copy
 * when a judge cannot be named.
 */
function substituteReplacedTitle(
  t: TFunction,
  update: JudgeReplacedUpdate,
  names: { outgoing: string | null; replacement: string | null; panel: string | null },
): string {
  const p = 'notifications.update.JUDGE_REPLACED.substitute';
  const { outgoing, replacement, panel } = names;
  const entry = named(update.entry?.displayName);
  if (outgoing && replacement && panel) {
    if (!update.entry) return t(`${p}.titleNamed`, { panel, outgoing, replacement });
    return entry ? t(`${p}.titleEntry`, { panel, outgoing, replacement, entry }) : t(`${p}.titleAnEntry`, { panel, outgoing, replacement });
  }
  // No panel name (final polish, F2): the judges and the entry are still named.
  if (outgoing && replacement) {
    if (!update.entry) return t(`${p}.titleJudges`, { outgoing, replacement });
    return entry ? t(`${p}.titleJudgesEntry`, { outgoing, replacement, entry }) : t(`${p}.titleJudgesAnEntry`, { outgoing, replacement });
  }
  if (panel) return entry ? t(`${p}.titlePanelEntry`, { panel, entry }) : t(`${p}.titlePanel`, { panel });
  return t(`${p}.title`);
}

/**
 * A substitute judge seated on one entry (#39 slice 6, D3): "Gasabo panel:
 * substitute judge for Ubumwe Coffee Co.", then who scores it. Why the first
 * judge stepped aside is never said.
 */
function entrySubstituteCopy(t: TFunction, update: EntrySubstituteUpdate): { title: string; detail: string } {
  const p = 'notifications.update.ENTRY_SUBSTITUTE';
  const entry = named(update.entry?.displayName);
  const panel = named(update.panelName);
  const judge = named(update.judge?.displayName);
  const title =
    entry && panel
      ? t(`${p}.titleNamed`, { panel, entry })
      : entry
        ? t(`${p}.titleEntry`, { entry })
        : panel
          ? t(`${p}.titlePanel`, { panel })
          : t(`${p}.title`);
  const detail = judge ? t(`${p}.detail`, { judge }) : t(`${p}.detailGeneric`);
  return { title, detail };
}

/**
 * An entry moved to another panel (#39 slice 6, D5), as the mockup words it:
 * "Imena Dance Studio moved to Kicukiro panel", then "Place stays Gasabo.
 * Kicukiro panel scores it afresh." The place part is left out when the entry
 * has none on record. The organiser's reason is never here.
 */
function entryMovedCopy(t: TFunction, update: EntryMovedUpdate): { title: string; detail: string } {
  const p = 'notifications.update.ENTRY_MOVED';
  const entry = named(update.entry?.displayName);
  const panel = named(update.panelName);
  const place = named(update.placeLabel);
  const title =
    entry && panel
      ? t(`${p}.titleNamed`, { entry, panel })
      : entry
        ? t(`${p}.titleEntry`, { entry })
        : panel
          ? t(`${p}.titlePanel`, { panel })
          : t(`${p}.title`);
  const afresh = panel ? t(`${p}.afresh`, { panel }) : t(`${p}.afreshGeneric`);
  const detail = place ? `${t(`${p}.placeStays`, { place })} ${afresh}` : afresh;
  return { title, detail };
}

/** The outcome badge per variant (mockup): ok, refused, amber for changes, neutral grey for not applied. */
const SIGNOFF_BADGE: Record<SignoffBellVariant, NonNullable<BadgeProps['variant']>> = {
  approved: 'success',
  sentBack: 'danger',
  changes: 'warning',
  notApplied: 'gray',
};

/** Round events say the round; a scheduled one is only ever an open or a close. */
const ROUND_EVENTS = new Set(['stage.open', 'stage.close', 'stage.decide']);

/**
 * A decided sign-off (#46, I6; `screen:bell-sign-off-notifications`): the
 * outcome as a labelled badge, "Your request to <request> was …" ("The
 * scheduled <opening of …> …" for a scheduled request), and the detail —
 * the decider and what happened, their quoted comment, or why it could not be
 * applied. A decider who cannot be named reads "an approver"; a round event
 * whose round cannot be named falls back to the generic title rather than
 * an empty phrase.
 */
function signoffResolvedCopy(
  t: TFunction,
  update: SignoffResolvedUpdate,
): { title: string; detail?: string; badge?: { label: string; variant: NonNullable<BadgeProps['variant']> } } {
  const p = 'notifications.update.SIGNOFF_RESOLVED';
  const s = update.signoff;
  if (!s) return { title: t(`${p}.title`) };
  const round = named(update.stageName);
  const scheduled = s.scheduled && (s.event === 'stage.open' || s.event === 'stage.close');
  const request =
    ROUND_EVENTS.has(s.event) && !round
      ? null
      : scheduled
        ? t(`${p}.scheduledRequest.${s.event}`, { round })
        : t(`${p}.request.${s.event}`, { round, count: s.counts.total });
  const decider = named(s.decider?.displayName) ?? t(`${p}.unknownDecider`);
  const comment = named(s.comment) ?? '';
  const v = `${p}.${s.variant}`;
  const titled = (key: string): string => (request ? t(key, { request }) : t(`${p}.title`));
  const badge = { label: t(`${v}.label`), variant: SIGNOFF_BADGE[s.variant] };
  switch (s.variant) {
    case 'approved': {
      const done = [t(`${p}.done.${s.event}`, { count: s.counts.executed })];
      if (s.counts.stale > 0) done.push(t(`${v}.someStale`, { count: s.counts.stale }));
      return {
        badge,
        title: titled(scheduled ? `${v}.titleScheduled` : `${v}.title`),
        detail: t(`${v}.detail`, { decider, result: done.join(' ') }),
      };
    }
    case 'sentBack':
      return {
        badge,
        title: titled(scheduled ? `${v}.titleScheduled` : `${v}.title`),
        detail: t(`${v}.detail`, { decider, comment }),
      };
    case 'changes':
      return {
        badge,
        title: t(`${v}.title`),
        detail: request
          ? t(scheduled ? `${v}.detailScheduled` : `${v}.detail`, { request, decider, comment })
          : t(`${p}.sentBack.detail`, { decider, comment }),
      };
    case 'notApplied': {
      // A withdrawn entry is said as a withdrawal, naming the applicant when
      // the name can be read, rather than as the generic "had moved on".
      const withdrawnName = s.withdrawn ? named(s.withdrawn.displayName) : null;
      return {
        badge,
        title: titled(scheduled ? `${v}.titleScheduled` : `${v}.title`),
        detail: s.withdrawn
          ? withdrawnName
            ? t(`${v}.detailWithdrawn`, { name: withdrawnName })
            : t(`${v}.detailWithdrawnUnnamed`)
          : t(`${v}.detail`, { reason: t(`signoff.staleReason.${staleReasonCode(s.staleReason)}`) }),
      };
    }
  }
}

/**
 * One bell row (spec AC3.0): urgency dot and word, title, detail line,
 * "Award · Round" context, relative time, and exactly one action. The urgency
 * is said in words as well as colour; there is no manual dismiss.
 */
function BellRow({
  tone,
  word,
  title,
  detail,
  context,
  contextLink,
  when,
  unread = false,
  highlight = false,
  badge,
  side,
}: {
  tone: Tone;
  /** The urgency word (or "Just now") beside the dot; omitted on plain Updates. */
  word?: string | undefined;
  title: string;
  detail?: string | undefined;
  context: string;
  /** Appended to the context line after its separator (the conflict row's panel link). */
  contextLink?: ReactNode;
  when: string | null;
  unread?: boolean;
  highlight?: boolean;
  /** An outcome label above the title (a decided sign-off), said in words as well as colour. */
  badge?: { label: string; variant: NonNullable<BadgeProps['variant']> } | undefined;
  /** The row's one action, or the "Expired" pill that replaces it. */
  side: ReactNode;
}) {
  const { t } = useTranslation();
  const wordTone = tone === 'read' ? 'text-muted-foreground' : WORD[tone];
  return (
    <li
      className={cn(
        'relative flex gap-2.5 border-t py-3 pl-[18px] pr-4 first:border-t-0',
        "before:absolute before:inset-y-2.5 before:left-0 before:w-[3px] before:rounded-r-[3px] before:content-['']",
        RULE[tone],
        highlight && 'bg-primary/5 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-500',
      )}
    >
      <div className="min-w-0 flex-1">
        {word ? (
          <p className={cn('flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide', wordTone)}>
            <span className="size-[7px] shrink-0 rounded-full bg-current" aria-hidden />
            {word}
          </p>
        ) : null}
        {badge ? (
          <Badge variant={badge.variant} data-variant={badge.variant} className={cn(word && 'mt-1')}>
            {badge.label}
          </Badge>
        ) : null}
        <p className={cn('mt-0.5 text-[13px] leading-snug', tone === 'read' ? 'font-medium text-muted-foreground' : 'font-semibold')}>
          {title}
        </p>
        {detail ? <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{detail}</p> : null}
        <p className="mt-1 text-[11.5px] text-muted-foreground">
          {contextLink ? (
            <>
              {t('notifications.context', { award: context, round: '' })}
              {contextLink}
            </>
          ) : (
            context
          )}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        <span className="flex items-center gap-2">
          {when ? <span className="whitespace-nowrap text-[11px] text-muted-foreground">{when}</span> : null}
          {unread ? (
            <span className="size-2 rounded-full bg-primary">
              <span className="sr-only">{t('notifications.unread')}</span>
            </span>
          ) : null}
        </span>
        {side}
      </div>
    </li>
  );
}

function RowAction({ href, label, primary, onClick }: { href: string; label: string; primary: boolean; onClick: () => void }) {
  return (
    <Button asChild size="sm" variant={primary ? 'default' : 'outline'} className="h-7 rounded-lg px-2.5">
      <Link to={href} onClick={onClick}>
        {label}
      </Link>
    </Button>
  );
}

/** A Needs-action row. Its action navigates and closes the bell (`onNavigate`). */
export function NeedsActionRow({ item, onNavigate }: { item: NeedsActionItem; onNavigate: () => void }) {
  const { t, i18n } = useTranslation();
  const tone = TONE_OF[item.urgency];
  const common = {
    tone,
    word: t(`notifications.urgency.${item.urgency}`),
    when: relativeTime(item.occurredAt, i18n.language),
  };
  const action = (label: string) => (
    <RowAction href={bellHref(item)} label={label} primary={tone === 'danger'} onClick={onNavigate} />
  );

  switch (item.type) {
    case 'conflicts.awaiting_decision':
      return (
        <BellRow
          {...common}
          title={t('notifications.item.conflicts.awaiting_decision.title', {
            judge: item.judgeName ?? /* an evaluator no longer on the team — never a raw id */ t('evaluations.coi.unknownJudge'),
          })}
          detail={t('notifications.item.conflicts.awaiting_decision.detail', {
            entry: item.entryName ?? /* an applicant no longer on file */ t('evaluations.coi.unknownApplicant'),
          })}
          context={joinContext(t, [item.awardName, item.stageName])}
          contextLink={
            // The panel the conflict sits on; none once that panel is archived (both null).
            item.panelId && item.panelName ? (
              <Link
                to={panel360Href(item.awardId, item.stageId, item.panelId)}
                onClick={onNavigate}
                className="font-medium text-primary underline-offset-4 hover:underline focus-visible:underline"
                aria-label={t('notifications.item.conflicts.awaiting_decision.panelLabel', { panel: item.panelName })}
              >
                {/* {{panel}} → */ t('notifications.item.conflicts.awaiting_decision.panel', { panel: item.panelName })}
              </Link>
            ) : undefined
          }
          side={action(t('notifications.item.conflicts.awaiting_decision.action'))}
        />
      );
    case 'approvals.awaiting_decision':
      return (
        <BellRow
          {...common}
          title={t('notifications.item.approvals.awaiting_decision.title')}
          detail={t('notifications.item.approvals.awaiting_decision.detail', {
            event: t(`approvals.event.${item.gateEvent}`, { defaultValue: item.gateEvent }),
          })}
          context={joinContext(t, [item.awardName, item.stageName])}
          side={action(t('notifications.item.approvals.awaiting_decision.action'))}
        />
      );
    case 'invitations.judges':
      return (
        <BellRow
          {...common}
          title={t('notifications.item.invitations.judges.title', { count: item.notAccepted })}
          // M ≤ N, so "0 have lapsed" is simply not said (AC3c.2).
          detail={item.lapsed > 0 ? t('notifications.item.invitations.judges.detail', { count: item.lapsed }) : undefined}
          context={t('notifications.item.invitations.judges.context')}
          side={action(t('notifications.item.invitations.judges.action'))}
        />
      );
  }
}

/**
 * An Update row. Its action marks the row read, then navigates and closes the
 * bell (AC5.1). An export past `expiresAt` shows "Expired" and no link — worked
 * out here, so expiry needs no push (AC4b.4).
 */
export function UpdateRow({
  update,
  highlight,
  now,
  onOpen,
  onNavigate,
}: {
  update: StaffUpdate;
  /** Pushed in while the bell was open: "Just now" and a tint. */
  highlight: boolean;
  now: number;
  onOpen: (update: StaffUpdate) => void;
  onNavigate: () => void;
}) {
  const { t, i18n } = useTranslation();
  const unread = update.readAt === null;
  const common = {
    tone: (unread ? 'info' : 'read') as Tone,
    word: highlight ? t('notifications.justNow') : undefined,
    when: relativeTime(update.createdAt, i18n.language, now),
    unread,
    highlight,
  };
  const open = (label: string) => (
    <RowAction
      href={bellHref(update)}
      label={label}
      primary={false}
      onClick={() => {
        onOpen(update);
        onNavigate();
      }}
    />
  );

  if (update.kind === 'JUDGING_STARTED') {
    return (
      <BellRow
        {...common}
        title={t('notifications.update.JUDGING_STARTED.title')}
        detail={t('notifications.update.JUDGING_STARTED.detail')}
        context={joinContext(t, [update.awardName, update.stageName])}
        side={open(t('notifications.update.JUDGING_STARTED.action'))}
      />
    );
  }

  if (update.kind === 'PANELS_GENERATED') {
    return (
      <BellRow
        {...common}
        {...panelsGeneratedCopy(t, i18n.resolvedLanguage, update)}
        context={joinContext(t, [update.awardName, update.stageName])}
        side={open(t('notifications.update.PANELS_GENERATED.action'))}
      />
    );
  }

  if (
    update.kind === 'JUDGE_ADDED_LATE' ||
    update.kind === 'JUDGE_REPLACED' ||
    update.kind === 'ENTRY_SUBSTITUTE' ||
    update.kind === 'ENTRY_MOVED'
  ) {
    // Each opens the panel's History in Panel 360 (bellHref); with no panel, the round's judging.
    const toPanel = !!update.panelId && !!update.stageId;
    const copy =
      update.kind === 'JUDGE_ADDED_LATE'
        ? judgeAddedLateCopy(t, update)
        : update.kind === 'JUDGE_REPLACED'
          ? judgeReplacedCopy(t, update)
          : update.kind === 'ENTRY_SUBSTITUTE'
            ? entrySubstituteCopy(t, update)
            : entryMovedCopy(t, update);
    return (
      <BellRow
        {...common}
        {...copy}
        context={joinContext(t, [update.awardName, update.stageName])}
        side={open(t(toPanel ? `notifications.update.${update.kind}.action` : `notifications.update.${update.kind}.actionRound`))}
      />
    );
  }

  if (update.kind === 'SIGNOFF_RESOLVED') {
    return (
      <BellRow
        {...common}
        {...signoffResolvedCopy(t, update)}
        context={joinContext(t, [update.awardName, update.stageName])}
        side={open(t('notifications.update.SIGNOFF_RESOLVED.action'))}
      />
    );
  }

  const expiresAt = update.expiresAt ? Date.parse(update.expiresAt) : null;
  const expired = expiresAt !== null && expiresAt <= now;
  const kind = t(`notifications.exportKind.${update.exportKind ?? 'APPLICATIONS'}`);
  const until =
    expiresAt !== null && !expired
      ? t('notifications.update.EXPORT_READY.availableUntil', {
          date: new Date(expiresAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' }),
        })
      : null;
  return (
    <BellRow
      {...common}
      title={exportReadyTitle(t, update)}
      detail={
        update.rowCount === null
          ? t('notifications.update.EXPORT_READY.detailNoCount', { kind })
          : t('notifications.update.EXPORT_READY.detail', { kind, count: update.rowCount })
      }
      context={joinContext(t, [update.awardName, update.stageName, until])}
      side={
        expired ? (
          <span className="inline-flex rounded-md bg-muted px-[7px] py-px text-[11px] font-semibold text-muted-foreground">
            {t('notifications.update.EXPORT_READY.expired')}
          </span>
        ) : (
          open(t('notifications.update.EXPORT_READY.action'))
        )
      }
    />
  );
}

/**
 * A ready Panel report says what it covers (C-4): the panel by name, or the
 * whole round (`panelName` and `panelId` null). Any other export, and a Panel report payload
 * from before `panelName` was sent (undefined), keeps the generic title.
 */
function exportReadyTitle(t: TFunction, update: ExportReadyUpdate): string {
  if (update.exportKind === 'PANEL_REPORT') {
    if (typeof update.panelName === 'string' && update.panelName.trim() !== '') {
      return t('notifications.update.EXPORT_READY.titlePanel', { panel: update.panelName });
    }
    // Null name AND no panel id: the round. A null name beside an id (a panel since archived) is not the round.
    if (update.panelName === null && (update.panelId ?? null) === null) return t('notifications.update.EXPORT_READY.titleRound');
  }
  return t('notifications.update.EXPORT_READY.title');
}
